import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";

export interface CurrentSubscription {
  id: string;
  plan: string;
  status: string;
  billingCycle?: string;
  currentPeriodEnd?: string | null;
  cancelAt?: string | null;
}

const TEXTS = {
  pt: {
    renews: (d: string) => `Renova sozinho em ${d}.`,
    ends: (d: string) => `Não renova. O plano vale até ${d}; depois a conta passa para o grátis, sem cobrança. Nada é apagado.`,
    on: "Renovação ligada",
    off: "Renovação cancelada",
    cancel: "Cancelar renovação",
    reactivate: "Reativar renovação",
    confirmTitle: "Cancelar a renovação?",
    confirmBody: (d: string) => `O plano continua valendo até ${d}. Depois a conta passa para o plano grátis, sem nova cobrança. Seus documentos ficam. Dá para reativar a qualquer momento antes dessa data.`,
    back: "Voltar",
    cancelled: "Renovação cancelada",
    cancelledBody: (d: string) => `O plano vale até ${d}. Mandamos a confirmação por e-mail.`,
    reactivated: "Renovação reativada",
    reactivatedBody: (d: string) => `O plano renova em ${d}.`,
    failed: "Não deu certo agora. Tente de novo em alguns minutos.",
  },
  en: {
    renews: (d: string) => `Renews automatically on ${d}.`,
    ends: (d: string) => `Does not renew. The plan is valid until ${d}; then the account moves to the free plan, with no charge. Nothing is deleted.`,
    on: "Renewal on",
    off: "Renewal cancelled",
    cancel: "Cancel renewal",
    reactivate: "Turn renewal back on",
    confirmTitle: "Cancel the renewal?",
    confirmBody: (d: string) => `The plan stays valid until ${d}. Then the account moves to the free plan, with no new charge. Your documents stay. You can turn it back on any time before that date.`,
    back: "Back",
    cancelled: "Renewal cancelled",
    cancelledBody: (d: string) => `The plan is valid until ${d}. We sent the confirmation by e-mail.`,
    reactivated: "Renewal back on",
    reactivatedBody: (d: string) => `The plan renews on ${d}.`,
    failed: "That did not work now. Try again in a few minutes.",
  },
};

/** Settings > Plano: whether the paid plan renews, with one click to cancel the renewal or turn it back on. */
export function RenewalControl({ subscription }: { subscription: CurrentSubscription | null | undefined }) {
  const { language } = useLanguage();
  const tx = TEXTS[language === "en" ? "en" : "pt"];
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!subscription || subscription.id === "free" || subscription.status !== "active" || !subscription.currentPeriodEnd) return null;
  const until = new Date(subscription.cancelAt || subscription.currentPeriodEnd).toLocaleDateString(language === "en" ? "en-US" : "pt-BR");
  const cancelled = !!subscription.cancelAt;

  const call = async (action: "cancel" | "reactivate") => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl(`/api/v1/subscriptions/${action}`), { method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error();
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/subscriptions/current"] });
      toast(action === "cancel" ? { title: tx.cancelled, description: tx.cancelledBody(until) } : { title: tx.reactivated, description: tx.reactivatedBody(until) });
    } catch {
      toast({ title: tx.failed, variant: "destructive" });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  return (
    <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${cancelled ? "border-amber-500/40 bg-amber-500/5" : "bg-muted/30"}`} data-testid="renewal-control">
      <div className="flex min-w-0 items-start gap-3">
        <CalendarClock className={`mt-0.5 h-5 w-5 shrink-0 ${cancelled ? "text-amber-600" : "text-muted-foreground"}`} />
        <div className="min-w-0 space-y-1">
          <Badge variant={cancelled ? "outline" : "secondary"} data-testid="renewal-state">{cancelled ? tx.off : tx.on}</Badge>
          <p className="text-sm text-muted-foreground">{cancelled ? tx.ends(until) : tx.renews(until)}</p>
        </div>
      </div>
      {cancelled ? (
        <Button size="sm" disabled={busy} onClick={() => void call("reactivate")} data-testid="button-reactivate-renewal">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}{tx.reactivate}
        </Button>
      ) : (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirm(true)} data-testid="button-cancel-renewal">{tx.cancel}</Button>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tx.confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{tx.confirmBody(until)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{tx.back}</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); void call("cancel"); }} data-testid="button-confirm-cancel-renewal">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{tx.cancel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
